/**
 * Shop settings and exchange rates.
 *
 * Loaded from the server when there is a connection and kept in the device cache
 * so the till still knows the VAT rate, the rounding rule and today's rates when
 * the internet is down. Changing them needs a connection, by design.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { DEFAULT_CURRENCIES, DEFAULT_SETTINGS, type Currency, type StoreSettings } from '@pos/shared';
import { setPrintRoute } from '../printing.js';
import { api, getToken } from '../api.js';
import { applyTheme, watchSystemTheme } from '../theme.js';
import { getMeta, setMeta } from '../db.js';
import { useI18n } from '../i18n/index.js';
import { useAuth } from './auth.js';
import type { FormatOpts } from '../format.js';
import * as F from '../format.js';

interface SettingsCtx {
  /**
   * What the till should do. A shop switch whose module the shop does not have
   * reads as off, so no screen waits on a shift nobody can open.
   */
  settings: StoreSettings;
  /** Exactly as saved - for the Settings screen, so saving never rewrites a switch. */
  rawSettings: StoreSettings;
  currencies: Currency[];
  ils: Currency;
  reload: () => Promise<void>;
  save: (patch: Partial<StoreSettings>) => Promise<void>;
  saveRate: (code: string, rate: number) => Promise<void>;
  fmt: FormatOpts;
  money: (agorot: number, code?: string) => string;
  amount: (agorot: number, code?: string) => string;
  num: (n: number) => string;
  date: (iso: string | Date) => string;
  time: (iso: string | Date) => string;
  dateTime: (iso: string | Date) => string;
}

const Ctx = createContext<SettingsCtx | null>(null);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const { lang } = useI18n();
  const { user, hasFeature } = useAuth();
  const [settings, setSettings] = useState<StoreSettings>(DEFAULT_SETTINGS);
  const [currencies, setCurrencies] = useState<Currency[]>(DEFAULT_CURRENCIES);

  const reload = useCallback(async () => {
    // Before anyone has signed in there is nothing to ask for, and asking would
    // only produce a 401 in the console on every load.
    if (!getToken()) {
      const cached = await getMeta<StoreSettings>('settings');
      const cachedCurrencies = await getMeta<Currency[]>('currencies');
      if (cached) setSettings({ ...DEFAULT_SETTINGS, ...cached });
      if (cachedCurrencies?.length) setCurrencies(cachedCurrencies);
      return;
    }
    try {
      const [s, c] = await Promise.all([
        api<StoreSettings>('/admin/settings'),
        api<Currency[]>('/admin/currencies'),
      ]);
      setSettings({ ...DEFAULT_SETTINGS, ...s });
      setCurrencies(c.length ? c : DEFAULT_CURRENCIES);
      await setMeta('settings', s);
      await setMeta('currencies', c);
    } catch {
      // Offline: fall back to whatever the device last cached.
      const s = await getMeta<StoreSettings>('settings');
      const c = await getMeta<Currency[]>('currencies');
      if (s) setSettings({ ...DEFAULT_SETTINGS, ...s });
      if (c?.length) setCurrencies(c);
    }
  }, []);

  // Re-read once somebody signs in: before that there is nothing to ask for.
  useEffect(() => {
    void reload();
  }, [reload, user?.id]);

  /*
   * Paint the shop's look. This runs for the cached settings too, so a till
   * that opens offline still comes up in the right theme. While the choice is
   * "follow the device", listen for the device changing its mind - at sunset,
   * or when someone flips the switch in Control Centre.
   */
  useEffect(() => {
    applyTheme(settings.theme, settings.accent);
    if (settings.theme !== 'auto') return;
    return watchSystemTheme(() => applyTheme(settings.theme, settings.accent));
  }, [settings.theme, settings.accent]);

  const save = useCallback(async (patch: Partial<StoreSettings>) => {
    const s = await api<StoreSettings>('/admin/settings', { method: 'PUT', body: patch });
    setSettings({ ...DEFAULT_SETTINGS, ...s });
    await setMeta('settings', s);
  }, []);

  const saveRate = useCallback(async (code: string, rate: number) => {
    const list = await api<Currency[]>(`/admin/currencies/${code}`, { method: 'PUT', body: { rate } });
    setCurrencies(list);
    await setMeta('currencies', list);
  }, []);

  const fmt = useMemo<FormatOpts>(
    () => ({ lang, arabicDigits: settings.arabicDigits, currencies }),
    [lang, settings.arabicDigits, currencies],
  );

  // The browser tab (and the taskbar of an installed till) wears the shop's logo.
  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) return;
    if (!link.dataset.original) link.dataset.original = link.href;
    link.href = settings.logoDataUrl || link.dataset.original;
  }, [settings.logoDataUrl]);

  // Every print follows the shop's printer choices from the moment they arrive.
  useEffect(() => setPrintRoute(settings), [settings]);

  const effective = useMemo<StoreSettings>(
    () => ({
      ...settings,
      shiftsEnabled: settings.shiftsEnabled && hasFeature('shifts'),
      customerTabsEnabled: settings.customerTabsEnabled && hasFeature('customers'),
    }),
    [settings, hasFeature],
  );

  const value = useMemo<SettingsCtx>(
    () => ({
      settings: effective,
      rawSettings: settings,
      currencies,
      ils: currencies.find((c) => c.code === 'ILS') ?? DEFAULT_CURRENCIES[0],
      reload,
      save,
      saveRate,
      fmt,
      money: (a, code) => F.money(a, fmt, code),
      amount: (a, code) => F.amount(a, fmt, code),
      num: (n) => F.num(n, fmt),
      date: (d) => F.date(d, fmt),
      time: (d) => F.time(d, fmt),
      dateTime: (d) => F.dateTime(d, fmt),
    }),
    [effective, settings, currencies, reload, save, saveRate, fmt],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSettings(): SettingsCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useSettings used outside the provider');
  return c;
}
